import { outlinedInputClasses } from '@mui/material';
import { createTheme } from '@mui/material/styles';

export const lightGray = '#8293AC1A';
export const gray = '#465063';

export default createTheme({
  typography: {
    allVariants: {
      fontFamily: 'Montserrat',
      lineHeight: '1.4',
    },
    gradient: {
      fontWeight: 800,
      backgroundImage:
        'linear-gradient(109.15deg, #FF006B 9.64%, #8F00FF 73.1%, #01A4FF 124.91%)',
      backgroundClip: 'text',
      textFillColor: 'transparent',
      boxDecorationBreak: 'clone',
    },
    h1: {
      fontSize: '24px',
      fontWeight: 800,
    },
    h2: {
      fontSize: '16px',
      fontWeight: 800,
      textAlign: 'center',
    },
    body1: {
      fontWeight: 500,
      fontSize: '14px',
    },
    fontFamily: [
      'Montserrat',
      '-apple-system',
      'BlinkMacSystemFont',
      'Segoe UI',
      'Roboto',
      'Oxygen',
      'Ubuntu',
      'Cantarell',
      'Fira Sans',
      'Droid Sans',
      'Helvetica Neue',
      'Montserrat',
      'sans-serif',
    ].join(','),
  },
  palette: {
    primary: {
      main: 'rgba(94, 57, 255, 1)',
    },
    secondary: { main: 'rgba(235, 47, 83, 1)' },
    success: {
      main: '#00C891',
    },
    error: {
      main: '#FC4848',
    },
    common: {
      borderColor: 'red',
    },
    text: {
      primary: '#02082D',
      secondary: 'rgba(70, 80, 99, 1)',
      disabled: 'rgba(144, 150, 161, 1)',
    },
    grey: {
      main: '#8293AC1F',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        a: {
          color: 'inherit',
          textDecoration: 'underline',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: ({ theme, ownerState }) => ({
          transition: 'border-color 0.2s',
          borderColor: ownerState?.color
            ? 'rgba(235, 235, 238, 1)'
            : getStateColor(ownerState, theme),
        }),
        root: ({ theme, ownerState }) => {
          const stateColor = getStateColor(ownerState, theme);

          return {
            borderRadius: 12,
            [`&:hover .${outlinedInputClasses.notchedOutline}, &.Mui-focused .${outlinedInputClasses.notchedOutline}`]:
              {
                borderColor: stateColor,
                borderWidth: 2,
              },
          };
        },
        input: {
          '&::placeholder': {
            color: (theme) => theme.palette.text.secondary,
          },
          fontSize: '16px',
          fontWeight: 500,
          padding: '12px 16px',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: '16px',
          borderRadius: '12px',
          boxShadow: '0px 1px 12px 0px rgba(47, 47, 55, 0.06)',
          fontSize: '16px',
          fontWeight: 600,
          outline: '2px solid transparent',
          '&:hover': {
            backgroundColor: 'white !important',
          },
          '&.Mui-selected': {
            backgroundColor: 'white',
            outlineColor: `${theme.palette.primary.main}`,
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        variant: 'contained',
      },
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontSize: '16px',
          fontWeight: 600,
          lineHeight: '20px',
          textTransform: 'capitalize',
          color: 'rgba(2, 8, 45, 1)',
          borderColor: 'rgba(2, 8, 45, 1)',
        },
        sizeLarge: {
          height: 60,
        },
        containedPrimary: {
          backgroundColor: 'rgba(2, 8, 45, 1)',
          color: 'white',
          '&.Mui-disabled': {
            color: '#ffffff',
          },
          '&:hover': {
            backgroundColor: 'black',
          },
        },
        containedSecondary: {
          color: '#ffffff',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          padding: 0,
          borderRadius: 0,
        },
      },
    },
    MuiContainer: {
      styleOverrides: { root: { padding: '0 20px', paddingBottom: '40px' } },
    },
    MuiRadio: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiSvgIcon-root': {
            fontSize: 22,
          },
          '&.Mui-checked': {
            color: '#00C891',
          },
        }),
      },
    },
    MuiPaper: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          padding: '10px 16px',
          borderRadius: 12,
          border: '1px solid',
          borderColor: '#80839633',
          boxShadow: 'unset',
          boxSizing: 'border-box',
        },
      },
      variants: [
        {
          props: { variant: 'success' },
          style: {
            background: 'rgba(234, 253, 240, 1)',
            borderColor: 'rgba(164, 243, 203, 1)',
            color: 'rgba(52, 69, 58, 1)',
          },
        },
      ],
    },
  },
});

function getStateColor(ownerState, theme) {
  if (ownerState?.error) {
    return theme.palette.error.main;
  }

  if (ownerState?.color && theme.palette[ownerState.color]) {
    return theme.palette[ownerState.color].main;
  }

  return theme.palette.primary.main;
}
